import React, { memo, useCallback, useMemo, useRef } from 'react'
import { VariableSizeList as ListBase } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import InfiniteLoaderBase from 'react-window-infinite-loader'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import ScrollListItem from './scrollListItem'
import { Expression } from '~/prix/types/expression'
import { ZodSchemaInfo } from '~/prix/types/zodType'
import { ViewListItem } from '~/packages/general/view/list/viewList.typing'

const InfiniteLoader = InfiniteLoaderBase as unknown as React.ComponentType<any>
const List = ListBase as unknown as React.ComponentType<any>

interface ScrollListProps {
  variables: ViewListItem[]
  currentBaseLayer: Layer
  handleSelectVariable: (variable: ViewListItem) => void
  onLoadMoreItems: () => Promise<void>
  nextPages: number
  apiIsLoading: boolean
  getFilter: (expression: Record<number, Expression> | undefined, index: string) => void
  filters: Expression[] | undefined
  selectedVariableIsFilterableBy: ZodSchemaInfo | undefined
}

function ScrollList({
  variables,
  currentBaseLayer,
  handleSelectVariable,
  onLoadMoreItems,
  nextPages,
  apiIsLoading,
  getFilter,
  filters,
  selectedVariableIsFilterableBy,
}: ScrollListProps) {
  const hasNextPages = nextPages > 0
  const itemCount = hasNextPages ? variables.length + 1 : variables.length
  const isItemLoaded = useCallback(
    (index: number) => {
      return index < variables.length
    },
    [variables.length],
  )
  const listRef = useRef<any>(null)
  const initialSize = 150
  const sizes = useMemo(
    () => new Array(variables.length).fill(initialSize),
    [variables.length, initialSize],
  )

  const getItemSize = (index: number) => {
    return sizes[index]
  }
  const loadMoreItems = useCallback(async () => {
    if (!hasNextPages || apiIsLoading) {
      return
    }

    await onLoadMoreItems()
  }, [apiIsLoading, hasNextPages, onLoadMoreItems])

  const setListRefs = useCallback(
    (instance: any, loaderRef: React.Ref<any>) => {
      listRef.current = instance

      if (typeof loaderRef === 'function') {
        loaderRef(instance)
        return
      }

      if (loaderRef && 'current' in loaderRef) {
        ;(loaderRef as React.MutableRefObject<any>).current = instance
      }
    },
    [],
  )

  return (
    <AutoSizer>
      {({ height, width }) => (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
          threshold={0}
        >
          {({ onItemsRendered, ref }: any) => (
            <List
              height={height}
              itemCount={itemCount}
              itemSize={getItemSize}
              width={width}
              onItemsRendered={onItemsRendered}
              ref={(instance: any) => setListRefs(instance, ref)}
            >
              {({ index, style }: any) => (
                <ScrollListItem
                  index={index}
                  style={style}
                  isItemLoaded={isItemLoaded}
                  variables={variables}
                  currentBaseLayer={currentBaseLayer}
                  handleSelectVariable={handleSelectVariable}
                  listRef={listRef}
                  sizes={sizes}
                  selectedVariableIsFilterableBy={selectedVariableIsFilterableBy}
                  isApiLoading={apiIsLoading}
                  getFilter={getFilter}
                  filters={filters}
                />
              )}
            </List>
          )}
        </InfiniteLoader>
      )}
    </AutoSizer>
  )
}

export default memo(ScrollList)
